import React from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { COLOR } from '@src/styles/color';
import i18n from '@src/languages';
import CustomButton from '@src/new-components/Button';
import { ACTION_TYPE, BANNER_TYPE } from '@src/constants/banner';
import { LANGUAGE } from '@src/constants';
import CustomTypography from '@src/new-components/Typography';
import ROUTES from '@src/constants/route';
import { TOP_PAID_EVENT_CODES_HASH } from '@src/constants/topPaidEvent';
import useTopPaidEvent from '@src/hooks/useTopPaidEvent';
import { pickEventRewardCodes } from '@src/services/topPaidEvent';
import TopUpBanner from './TopUpBanner';
import SubscriptionSuccessPopup from './SubscriptionSuccessPopup';
import RewardCodeGrid from './RewardCodeGrid';

// Event reward codes plus the VIP invite, which moves into a callout once codes are shown.
const StyledRewardContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
  marginTop: 4,

  '.vip-invite': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 12,
    backgroundColor: COLOR.backgroundPending,
  },
  '.vip-invite-icon': { fontSize: 24, flexShrink: 0, color: COLOR.blueV2 },
});

const JoinGroupZalo = ({
  isTopUpPackage,
  open,
  bannerIndex,
  banner,
  description,
  closeAction,
  onCloseBanner,
  onActionChange,
}) => {
  const { language } = i18n;
  const { t } = useTranslation();
  const history = useHistory();
  const { config, isRunning } = useTopPaidEvent();
  // Set by the PAID websocket message alongside the rest of this popup's data.
  const rewardCodes = useSelector(
    (state) =>
      state.banner.actionBanner?.[BANNER_TYPE.JOIN_ZALO_GROUP]?.rewardCodes,
  );

  const eventCodes =
    isRunning && config.rewardPopupEnabled
      ? pickEventRewardCodes(rewardCodes, config.eventId)
      : [];
  const hasEventCodes = eventCodes.length > 0;

  const handleClose = (action) => (event, reason) => {
    const { url, notShowAgain, type } = action;
    if (reason === 'backdropClick') return;
    if (onActionChange) onActionChange(url, type);
    // TODO: check not show again from banner data
    onCloseBanner(bannerIndex, notShowAgain);
  };

  const handleViewCodes = () => {
    handleClose(closeAction)();
    history.push(`${ROUTES.TOP_PAID_EVENT}${TOP_PAID_EVENT_CODES_HASH}`);
  };

  if (isTopUpPackage)
    return (
      <TopUpBanner
        handleClosePopup={handleClose(closeAction)}
        open={open}
        rewardCodes={eventCodes}
        onViewCodes={handleViewCodes}
      />
    );

  const descriptionText = (
    <CustomTypography
      variant="body-14-regular"
      color={COLOR.descriptionGray}
      style={{
        textAlign: hasEventCodes ? 'left' : 'center',
      }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html:
          description ||
          (banner.description &&
            (banner.description[language] || banner.description[LANGUAGE.EN])),
      }}
    />
  );

  return (
    <SubscriptionSuccessPopup
      open={open}
      title={banner.title[language] || banner.title[LANGUAGE.EN]}
      description={
        hasEventCodes ? (
          <StyledRewardContent>
            <RewardCodeGrid codes={eventCodes} />
            <div className="vip-invite">
              <InfoRoundedIcon className="vip-invite-icon" />
              {descriptionText}
            </div>
          </StyledRewardContent>
        ) : (
          descriptionText
        )
      }
      actions={[
        ...banner.actions.map((action, index) => (
          <CustomButton
            key={action[LANGUAGE.EN] || index}
            // With codes, "View my codes" below takes the primary slot.
            variant={index === 0 && !hasEventCodes ? 'contained' : 'outlined'}
            color={index === 0 && !hasEventCodes ? 'primary' : 'secondary'}
            onClick={handleClose({ ...action, type: ACTION_TYPE.BUTTON })}
            customStyle={{
              borderRadius: '8px',
              padding: '8px 24px',
              height: '40px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.indigo.default,
            }}
          >
            {action[language] || action[LANGUAGE.EN]}
          </CustomButton>
        )),
        hasEventCodes && (
          <CustomButton
            key="view-reward-codes"
            onClick={handleViewCodes}
            customStyle={{
              borderRadius: '8px',
              padding: '8px 24px',
              height: '40px',
            }}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.indigo.default,
            }}
          >
            {t('topPaidEventViewMyCodes')}
          </CustomButton>
        ),
      ]}
      onClose={handleClose(closeAction)}
    />
  );
};

export default JoinGroupZalo;
